import { Injectable, inject } from '@angular/core';
import { Resolve } from '@angular/router';
import { Observable, of, tap, combineLatest } from 'rxjs';

import { ContentService } from '../services/content/content.service';
import { ContentUtil } from '../utils/content/content.util';
import { deepUpdate } from '../../aem/aem-utils';
import { modifiedGranteeContent } from '../../content/zci/grantee-content-modified';
import { modifiedGranteeAlmostThereContent } from '../../content/zci/grantee-almost-there-content-modified';
import { modifiedGrantorContent } from '../../content/zci/grantor-content-modified';
import { modifiedGranteeWelcomeContent } from 'src/content/zci/grantee-welcome-content-modified';

@Injectable({
    providedIn: 'root',
})
export class ContentResolver implements Resolve<any> {
    private readonly contentService = inject(ContentService);


    /**
     * Resolves the necessary data before navigating to a route.
     * This method is typically used to fetch and prepare data required
     * by a component before it is loaded.
     *
     * @returns {Observable<any> | Promise<any> | any} The resolved data, which can be an observable, a promise, or a plain value.
     */
    resolve(): Observable<any> {
        if (
            ContentUtil.content &&
            ContentUtil.granteeFragmentsContent &&
            ContentUtil.granteeAlmostThereContent &&
            ContentUtil.granteeWelcomeContent &&
            ContentUtil.grantorFragmentContent
        ) {
            return of([
                ContentUtil.content,
                ContentUtil.granteeFragmentsContent,
                ContentUtil.granteeAlmostThereContent,
                ContentUtil.granteeWelcomeContent,
                ContentUtil.grantorFragmentContent,
            ]);
        }

        return combineLatest([
            this.contentService.getContent(),
            this.contentService.getGranteeGraphqlContent(),
            this.contentService.getGranteeAlmostThereGraphqlContent(),
            this.contentService.getGranteeWelcomeGraphqlContent(),
            this.contentService.getGrantorGraphqlContent(),
        ]).pipe(
            tap(
                ([
                    content,
                    granteeFragmentsContent,
                    granteeAlmostThereContent,
                    granteeWelcomeGraphqlContent,
                    grantorFragmentContent,
                ]) => {
                    ContentUtil.content = content;

                    ContentUtil.granteeFragmentsContent = deepUpdate(
                        modifiedGranteeContent,
                        granteeFragmentsContent,
                    );

                    ContentUtil.granteeAlmostThereContent = deepUpdate(
                        modifiedGranteeAlmostThereContent,
                        granteeAlmostThereContent,
                    );

                    ContentUtil.granteeWelcomeContent = deepUpdate(
                        modifiedGranteeWelcomeContent,
                        granteeWelcomeGraphqlContent,
                    );

                    ContentUtil.grantorFragmentContent = deepUpdate(
                        modifiedGrantorContent,
                        grantorFragmentContent,
                    );
                },
            ),
        );
    }
}
